import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "lucide-react";
import type React from "react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import {
	type PartnerActivationFormData,
	partnerActivationSchema,
} from "../schemas/partnerActivation";
import FieldError from "./FieldError";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import ValidationBanner from "./ValidationBanner";

interface PartnerActivationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (data: { agentId: string; mainOfficeId: string }) => void;
	walletName: string;
}

const PartnerActivationModal: React.FC<PartnerActivationModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	walletName,
}) => {
	const bannerRef = useRef<HTMLDivElement>(null);

	const form = useForm<PartnerActivationFormData>({
		resolver: zodResolver(partnerActivationSchema),
		defaultValues: {
			agentId: "",
			mainOfficeId: "",
		},
		mode: "onSubmit",
	});

	const handleClose = () => {
		form.reset({ agentId: "", mainOfficeId: "" });
		onClose();
	};

	const onValidSubmit = (data: PartnerActivationFormData) => {
		onSubmit({ agentId: data.agentId, mainOfficeId: data.mainOfficeId });
		form.reset({ agentId: "", mainOfficeId: "" });
	};

	const onInvalid = () => {
		bannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	const errors = form.formState.errors;
	const hasErrors = Object.keys(errors).length > 0;
	const errorMessages = hasErrors
		? Object.values(errors)
				.filter((e): e is { message?: string } => !!e && typeof e === "object")
				.map((e) => e.message)
				.filter((m): m is string => !!m)
		: [];

	return (
		<Dialog open={isOpen} onOpenChange={(v) => !v && handleClose()}>
			<DialogContent>
				<DialogHeader>
					<div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-(--color-primary) to-[#764ba2] text-white">
						<Check size={32} />
					</div>
					<DialogTitle>Activate Partner</DialogTitle>
					<p className="text-(--color-muted-foreground) text-sm">
						{walletName}
					</p>
				</DialogHeader>

				<form
					onSubmit={form.handleSubmit(onValidSubmit, onInvalid)}
					className="space-y-4"
				>
					<div ref={bannerRef}>
						{hasErrors && (
							<ValidationBanner
								message="Please fix the errors below."
								fields={errorMessages}
								aria-live="polite"
							/>
						)}
					</div>

					<p className="text-(--color-muted-foreground) text-sm">
						Enter the following information to activate the partner. These
						details will be used for production deployment.
					</p>

					<div className="space-y-1.5">
						<Label htmlFor="agentId">
							Agent ID <span className="text-(--color-error-500)">*</span>
						</Label>
						<Input
							id="agentId"
							{...form.register("agentId")}
							placeholder="Enter Agent ID"
							className={
								errors.agentId ? "border-(--color-error-500)" : undefined
							}
							aria-invalid={!!errors.agentId}
							aria-describedby={errors.agentId ? "agentId-error" : undefined}
						/>
						{errors.agentId?.message && (
							<FieldError id="agentId-error" message={errors.agentId.message} />
						)}
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="mainOfficeId">
							Main Office ID <span className="text-(--color-error-500)">*</span>
						</Label>
						<Input
							id="mainOfficeId"
							{...form.register("mainOfficeId")}
							placeholder="Enter Main Office ID"
							className={
								errors.mainOfficeId ? "border-(--color-error-500)" : undefined
							}
							aria-invalid={!!errors.mainOfficeId}
							aria-describedby={
								errors.mainOfficeId ? "mainOfficeId-error" : undefined
							}
						/>
						{errors.mainOfficeId?.message && (
							<FieldError
								id="mainOfficeId-error"
								message={errors.mainOfficeId.message}
							/>
						)}
					</div>

					<DialogFooter>
						<Button type="button" variant="outline" onClick={handleClose}>
							Cancel
						</Button>
						<Button type="submit">Activate Partner</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default PartnerActivationModal;
